import React, { useState, useMemo, useEffect, useRef } from 'react';
import { MdSearch, MdClose, MdCheckBox, MdCheckBoxOutlineBlank } from 'react-icons/md';
import './ClientFilter.css';

const ClientFilter = ({ clients = [], selectedClientIds = [], onSelectionChange }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Chiudi il dropdown quando si clicca fuori
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        triggerRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const filteredClients = useMemo(() => {
    if (!searchQuery.trim()) {
      return clients;
    }
    const query = searchQuery.toLowerCase().trim();
    return clients.filter(client => 
      `${client.nome} ${client.cognome}`.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query)
    );
  }, [clients, searchQuery]);

  const toggleClient = (clientId) => {
    const newSelection = selectedClientIds.includes(clientId)
      ? selectedClientIds.filter(id => id !== clientId)
      : [...selectedClientIds, clientId];
    onSelectionChange(newSelection);
  };

  const clearSelection = () => {
    onSelectionChange([]);
    setSearchQuery('');
  };

  const getSelectedClientsNames = () => {
    if (selectedClientIds.length === 0) return 'Tutti i clienti';
    if (selectedClientIds.length === 1) {
      const client = clients.find(c => c.id === selectedClientIds[0]);
      return client ? `${client.nome} ${client.cognome}` : '1 cliente';
    }
    return `${selectedClientIds.length} clienti selezionati`;
  };

  return (
    <div className="client-filter">
      <div className="cf-container">
        <div 
          ref={triggerRef}
          className="cf-trigger"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="cf-trigger-content">
            <MdSearch className="cf-search-icon" />
            <span className="cf-trigger-text">{getSelectedClientsNames()}</span>
          </div>
          {selectedClientIds.length > 0 && (
            <button
              className="cf-clear-btn"
              onClick={(e) => {
                e.stopPropagation();
                clearSelection();
              }}
              title="Rimuovi filtri"
            >
              <MdClose />
            </button>
          )}
        </div>

        {/* Badge dei clienti selezionati */}
        {selectedClientIds.length > 0 && (
          <div className="cf-selected-badges">
            {selectedClientIds.map(clientId => {
              const client = clients.find(c => c.id === clientId);
              if (!client) return null;
              return (
                <div key={clientId} className="cf-badge">
                  <span className="cf-badge-text">
                    {client.nome} {client.cognome}
                  </span>
                  <button
                    className="cf-badge-remove"
                    onClick={() => {
                      const newSelection = selectedClientIds.filter(id => id !== clientId);
                      onSelectionChange(newSelection);
                    }}
                    title="Rimuovi cliente"
                  >
                    <MdClose />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {isOpen && (
          <div ref={dropdownRef} className="cf-dropdown">
            <div className="cf-search-container">
              <MdSearch className="cf-search-icon-input" />
              <input
                type="text"
                className="cf-search-input"
                placeholder="Cerca cliente per nome, cognome o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
              {searchQuery && (
                <button
                  className="cf-clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Cancella ricerca"
                >
                  <MdClose />
                </button>
              )}
            </div>

            <div className="cf-actions">
              <button
                className="cf-action-btn cf-select-all"
                onClick={() => {
                  const allIds = filteredClients.map(c => c.id);
                  onSelectionChange(allIds);
                }}
              >
                Seleziona tutti
              </button>
              <button
                className="cf-action-btn cf-deselect-all"
                onClick={() => {
                  const filteredIds = filteredClients.map(c => c.id);
                  const newSelection = selectedClientIds.filter(id => !filteredIds.includes(id));
                  onSelectionChange(newSelection);
                }}
              >
                Deseleziona tutti
              </button>
            </div>

            <div className="cf-clients-list">
              {filteredClients.length === 0 ? (
                <div className="cf-no-results">
                  Nessun cliente trovato per "{searchQuery}"
                </div>
              ) : (
                filteredClients.map(client => {
                  const isSelected = selectedClientIds.includes(client.id);
                  return (
                    <div
                      key={client.id}
                      className={`cf-client-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => toggleClient(client.id)}
                    >
                      <div className="cf-checkbox">
                        {isSelected ? (
                          <MdCheckBox className="cf-checkbox-icon checked" />
                        ) : (
                          <MdCheckBoxOutlineBlank className="cf-checkbox-icon" />
                        )}
                      </div>
                      <div className="cf-client-info">
                        <div className="cf-client-name">
                          {client.nome} {client.cognome}
                        </div>
                        {client.email && (
                          <div className="cf-client-email">{client.email}</div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {selectedClientIds.length > 0 && (
              <div className="cf-footer">
                <span className="cf-selected-count">
                  {selectedClientIds.length} {selectedClientIds.length === 1 ? 'cliente selezionato' : 'clienti selezionati'}
                </span>
                <button
                  className="cf-apply-btn"
                  onClick={() => setIsOpen(false)}
                >
                  Applica
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientFilter;



